import React, { useState, useEffect } from 'react';
import { getAvatarUrl } from '../utils/avatarUtils';
import AvatarPreview from './ui/AvatarPreview';

const Avatar = ({
  blobName,
  alt = 'Avatar',
  size = 'md',
  className = '',
  fallbackText = null,
  showFallbackOnError = true,
  previewable = false,
}) => {
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(blobName));
  const [hasError, setHasError] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const sizeClasses = {
    xs: 'h-6 w-6',
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
    '2xl': 'h-20 w-20',
  };

  const textSizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg',
    '2xl': 'text-xl',
  };

  useEffect(() => {
    let cancelled = false;

    if (!blobName) {
      setAvatarUrl(null);
      setHasError(false);
      setIsLoading(false);
      return undefined;
    }

    // Already a full URL (OAuth / local path) — use directly
    if (/^https?:\/\//i.test(blobName) || blobName.startsWith('/')) {
      setAvatarUrl(blobName);
      setHasError(false);
      setIsLoading(false);
      return undefined;
    }

    const loadAvatar = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        const url = await getAvatarUrl(blobName);
        if (!cancelled) setAvatarUrl(url);
      } catch (error) {
        console.error('Failed to load avatar:', error);
        if (!cancelled) setHasError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadAvatar();
    return () => {
      cancelled = true;
    };
  }, [blobName]);

  const handleImageError = () => setHasError(true);
  const handleImageLoad = () => setHasError(false);

  const canPreview = previewable && Boolean(avatarUrl) && !hasError;

  const openPreview = (e) => {
    if (!canPreview) return;
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setPreviewOpen(true);
  };

  const wrapPreviewable = (node) => (
    <>
      {canPreview ? (
        <button
          type="button"
          onClick={openPreview}
          className="p-0 m-0 border-0 bg-transparent rounded-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          title="View photo"
          aria-label={`View photo of ${alt}`}
        >
          {node}
        </button>
      ) : (
        node
      )}
      <AvatarPreview
        open={previewOpen}
        src={avatarUrl}
        alt={alt}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );

  if (isLoading && blobName) {
    return (
      <div
        className={`${sizeClasses[size]} ${className} bg-gray-200 rounded-full animate-pulse flex items-center justify-center`}
      >
        <div className="w-3 h-3 bg-gray-400 rounded-full" />
      </div>
    );
  }

  if (avatarUrl && !hasError) {
    return wrapPreviewable(
      <img
        src={avatarUrl}
        alt={alt}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        className={`${sizeClasses[size]} ${className} rounded-full object-cover shrink-0`}
        onError={handleImageError}
        onLoad={handleImageLoad}
      />
    );
  }

  if (showFallbackOnError || !blobName) {
    const initialsFrom = (value) => {
      if (!value || typeof value !== 'string') return null;
      const cleaned = value.trim();
      if (!cleaned) return null;
      // Email → first character before @
      if (cleaned.includes('@')) {
        return cleaned[0].toUpperCase();
      }
      const words = cleaned.split(/\s+/).filter(Boolean);
      if (words.length >= 2) {
        return `${words[0][0]}${words[1][0]}`.toUpperCase();
      }
      return cleaned.slice(0, 2).toUpperCase();
    };

    // Always show initials (never dump a full name into the circle)
    const fallbackContent =
      initialsFrom(fallbackText) || initialsFrom(alt) || '?';

    return (
      <div
        className={`${sizeClasses[size]} ${className} bg-primary-100 rounded-full flex items-center justify-center overflow-hidden shrink-0`}
      >
        <span className={`${textSizeClasses[size]} font-medium text-primary-600 leading-none`}>
          {fallbackContent}
        </span>
      </div>
    );
  }

  return null;
};

export default Avatar;
